import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Form,
  Row,
  Col,
  Switch,
  Button,
  message,
  Divider,
  Typography,
  Select,
  InputNumber,
  Alert,
  Space,
  Upload,
  Avatar,
  Table,
  Input,
} from 'antd';
import {
  SettingOutlined,
  UserOutlined,
  BellOutlined,
  SecurityScanOutlined,
  DatabaseOutlined,
  GlobalOutlined,
  UploadOutlined,
  SaveOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import useStore from '../store/useStore';
import FormField from '../components/forms/FormField';
import RightDrawer from '../components/common/RightDrawer';

const { Title, Text } = Typography;
const { Option } = Select;

const Settings = () => {
  const { user, setUser } = useStore();
  const [loading, setLoading] = useState(false);
  const [generalForm] = Form.useForm();
  const [notificationForm] = Form.useForm();
  const [securityForm] = Form.useForm();
  const [systemForm] = Form.useForm();
  const [userForm] = Form.useForm();

  // Users & Roles state (mock local state for now)
  const [users, setUsers] = useState([
    { id: 'u1', name: 'Admin User', email: 'admin@example.com', role: 'Admin' },
    { id: 'u2', name: 'Store Manager', email: 'manager@example.com', role: 'Manager' },
  ]);
  const roleOptions = ['Admin', 'Manager', 'Staff', 'Viewer'];
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const showAddUser = () => {
    setEditingUser(null);
    userForm.resetFields();
    setUserDrawerOpen(true);
  };

  const showEditUser = (record) => {
    setEditingUser(record);
    userForm.setFieldsValue(record);
    setUserDrawerOpen(true);
  };

  const handleDeleteUser = (id) => {
    setUsers((prev) => prev.filter((u) => u.id !== id));
    message.success('User removed');
  };

  const handleSaveUser = async () => {
    const values = await userForm.validateFields();
    if (editingUser) {
      setUsers((prev) => prev.map((u) => (u.id === editingUser.id ? { ...editingUser, ...values } : u)));
      message.success('User updated');
    } else {
      setUsers((prev) => [...prev, { id: `u${Date.now()}`, ...values }]);
      message.success('User added');
    }
    setUserDrawerOpen(false);
    setEditingUser(null);
  };

  const userColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Role', dataIndex: 'role', key: 'role' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_,
        record) => (
        <Space>
          <Button size="small" onClick={() => showEditUser(record)}>Edit</Button>
          <Button size="small" danger onClick={() => handleDeleteUser(record.id)}>Delete</Button>
        </Space>
      ),
    },
  ];

  const [settings, setSettings] = useState({
    general: {
      companyName: 'QQINV Co.',
      companyEmail: 'admin@inventory.com',
      companyPhone: '+1 (555) 123-4567',
      companyAddress: '123 Business St, City, State 12345',
      timezone: 'America/New_York',
      dateFormat: 'MM/DD/YYYY',
      currency: 'USD',
      language: 'en',
    },
    notifications: {
      emailNotifications: true,
      pushNotifications: true,
      lowStockAlerts: true,
      orderUpdates: true,
      systemMaintenance: true,
      weeklyReports: false,
      monthlyReports: true,
      lowStockThreshold: 10,
    },
    security: {
      twoFactorAuth: false,
      sessionTimeout: 30,
      passwordExpiry: 90,
      loginAttempts: 5,
      ipWhitelist: false,
      auditLogging: true,
    },
    system: {
      autoBackup: true,
      backupFrequency: 'daily',
      dataRetention: 365,
      maintenanceMode: false,
      debugMode: false,
      cacheEnabled: true,
    },
  });

  useEffect(() => {
    // Initialize forms with current settings
    generalForm.setFieldsValue(settings.general);
    notificationForm.setFieldsValue(settings.notifications);
    securityForm.setFieldsValue(settings.security);
    systemForm.setFieldsValue(settings.system);
  }, [settings, generalForm, notificationForm, securityForm, systemForm]);

  const handleSaveSettings = async (category, values) => {
    try {
      setLoading(true);
      
      // Update local state
      setSettings(prev => ({
        ...prev,
        [category]: { ...prev[category], ...values }
      }));

      // In a real app, you would save to backend
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      message.success(`${category.charAt(0).toUpperCase() + category.slice(1)} settings saved successfully`);
    } catch (error) {
      message.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSettings = (category) => {
    const defaultSettings = {
      general: {
        companyName: '',
        companyEmail: '',
        companyPhone: '',
        companyAddress: '',
        timezone: 'America/New_York',
        dateFormat: 'MM/DD/YYYY',
        currency: 'USD',
        language: 'en',
      },
      notifications: {
        emailNotifications: true,
        pushNotifications: true,
        lowStockAlerts: true,
        orderUpdates: true,
        systemMaintenance: true,
        weeklyReports: false,
        monthlyReports: true,
        lowStockThreshold: 10,
      },
      security: {
        twoFactorAuth: false,
        sessionTimeout: 30,
        passwordExpiry: 90,
        loginAttempts: 5,
        ipWhitelist: false,
        auditLogging: true,
      },
      system: {
        autoBackup: true,
        backupFrequency: 'daily',
        dataRetention: 365,
        maintenanceMode: false,
        debugMode: false,
        cacheEnabled: true,
      },
    };

    setSettings(prev => ({
      ...prev,
      [category]: defaultSettings[category]
    }));

    // Reset form
    const forms = {
      general: generalForm,
      notifications: notificationForm,
      security: securityForm,
      system: systemForm,
    };
    forms[category].setFieldsValue(defaultSettings[category]);
    
    message.info(`${category.charAt(0).toUpperCase() + category.slice(1)} settings reset to defaults`);
  };

  const tabItems = [
    {
      key: 'general',
      label: (
        <span>
          <SettingOutlined />
          General
        </span>
      ),
      children: (
        <Card>
          <Form
            form={generalForm}
            layout="vertical"
            onFinish={(values) => handleSaveSettings('general', values)}
          >
            <Title level={4}>Company Information</Title>
            
            <Row gutter={16}>
              <Col span={12}>
                <FormField
                  name="companyName"
                  label="Company Name"
                  placeholder="Enter company name"
                />
              </Col>
              <Col span={12}>
                <FormField
                  name="companyEmail"
                  label="Company Email"
                  type="email"
                  placeholder="Enter company email"
                />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <FormField
                  name="companyPhone"
                  label="Company Phone"
                  placeholder="Enter company phone"
                />
              </Col>
              <Col span={12}>
                <FormField
                  name="timezone"
                  label="Timezone"
                  type="select"
                  options={[
                    { value: 'America/New_York', label: 'Eastern Time (ET)' },
                    { value: 'America/Chicago', label: 'Central Time (CT)' },
                    { value: 'America/Denver', label: 'Mountain Time (MT)' },
                    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
                    { value: 'UTC', label: 'UTC' },
                  ]}
                />
              </Col>
            </Row>

            <FormField
              name="companyAddress"
              label="Company Address"
              type="textarea"
              placeholder="Enter company address"
              rows={3}
            />

            <Divider />

            <Title level={4}>Localization</Title>

            <Row gutter={16}>
              <Col span={8}>
                <FormField
                  name="language"
                  label="Language"
                  type="select"
                  options={[
                    { value: 'en', label: 'English' },
                    { value: 'es', label: 'Spanish' },
                    { value: 'fr', label: 'French' },
                    { value: 'de', label: 'German' },
                  ]}
                />
              </Col>
              <Col span={8}>
                <FormField
                  name="currency"
                  label="Currency"
                  type="select"
                  options={[
                    { value: 'USD', label: 'US Dollar ($)' },
                    { value: 'EUR', label: 'Euro (€)' },
                    { value: 'GBP', label: 'British Pound (£)' },
                    { value: 'CAD', label: 'Canadian Dollar (C$)' },
                  ]}
                />
              </Col>
              <Col span={8}>
                <FormField
                  name="dateFormat"
                  label="Date Format"
                  type="select"
                  options={[
                    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
                    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
                    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
                  ]}
                />
              </Col>
            </Row>

            <div className="flex justify-between mt-6">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => handleResetSettings('general')}
              >
                Reset to Defaults
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
              >
                Save Changes
              </Button>
            </div>
          </Form>
        </Card>
      ),
    },
    {
      key: 'notifications',
      label: (
        <span>
          <BellOutlined />
          Notifications
        </span>
      ),
      children: (
        <Card>
          <Form
            form={notificationForm}
            layout="vertical"
            onFinish={(values) => handleSaveSettings('notifications', values)}
          >
            <Title level={4}>Notification Preferences</Title>
            
            <Alert
              message="Configure how you want to receive notifications"
              description="You can customize notification settings for different types of events."
              type="info"
              showIcon
              className="mb-6"
            />

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="emailNotifications" valuePropName="checked">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <Text strong>Email Notifications</Text>
                      <div className="text-sm text-gray-500">Receive notifications via email</div>
                    </div>
                    <Switch />
                  </div>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="pushNotifications" valuePropName="checked">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <Text strong>Push Notifications</Text>
                      <div className="text-sm text-gray-500">Receive browser push notifications</div>
                    </div>
                    <Switch />
                  </div>
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Title level={4}>Alert Types</Title>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="lowStockAlerts" valuePropName="checked">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <Text strong>Low Stock Alerts</Text>
                      <div className="text-sm text-gray-500">When inventory is running low</div>
                    </div>
                    <Switch />
                  </div>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="orderUpdates" valuePropName="checked">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <Text strong>Order Updates</Text>
                      <div className="text-sm text-gray-500">Status changes on orders</div>
                    </div>
                    <Switch />
                  </div>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="systemMaintenance" valuePropName="checked">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <Text strong>System Maintenance</Text>
                      <div className="text-sm text-gray-500">Scheduled maintenance notifications</div>
                    </div>
                    <Switch />
                  </div>
                </Form.Item>
              </Col>
              <Col span={12}>
                <FormField
                  name="lowStockThreshold"
                  label="Low Stock Threshold"
                  type="number"
                  min={1}
                  max={100}
                  placeholder="Enter threshold"
                />
              </Col>
            </Row>

            <Divider />

            <Title level={4}>Reports</Title>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="weeklyReports" valuePropName="checked">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <Text strong>Weekly Reports</Text>
                      <div className="text-sm text-gray-500">Receive weekly summary reports</div>
                    </div>
                    <Switch />
                  </div>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="monthlyReports" valuePropName="checked">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <Text strong>Monthly Reports</Text>
                      <div className="text-sm text-gray-500">Receive monthly summary reports</div>
                    </div>
                    <Switch />
                  </div>
                </Form.Item>
              </Col>
            </Row>

            <div className="flex justify-between mt-6">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => handleResetSettings('notifications')}
              >
                Reset to Defaults
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
              >
                Save Changes
              </Button>
            </div>
          </Form>
        </Card>
      ),
    },
    {
      key: 'security',
      label: (
        <span>
          <SecurityScanOutlined />
          Security
        </span>
      ),
      children: (
        <Card>
          <Form
            form={securityForm}
            layout="vertical"
            onFinish={(values) => handleSaveSettings('security', values)}
          >
            <Title level={4}>Authentication & Access</Title>
            
            <Alert
              message="Security Settings"
              description="Configure security policies and access controls for your system."
              type="warning"
              showIcon
              className="mb-6"
            />

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="twoFactorAuth" valuePropName="checked">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <Text strong>Two-Factor Authentication</Text>
                      <div className="text-sm text-gray-500">Require 2FA for all users</div>
                    </div>
                    <Switch />
                  </div>
                </Form.Item>
              </Col>
              <Col span={12}>
                <FormField
                  name="sessionTimeout"
                  label="Session Timeout (minutes)"
                  type="number"
                  min={5}
                  max={480}
                  placeholder="Enter timeout in minutes"
                />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <FormField
                  name="passwordExpiry"
                  label="Password Expiry (days)"
                  type="number"
                  min={30}
                  max={365}
                  placeholder="Enter expiry in days"
                />
              </Col>
              <Col span={12}>
                <FormField
                  name="loginAttempts"
                  label="Max Login Attempts"
                  type="number"
                  min={3}
                  max={10}
                  placeholder="Enter max attempts"
                />
              </Col>
            </Row>

            <Divider />

            <Title level={4}>Access Control</Title>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="ipWhitelist" valuePropName="checked">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <Text strong>IP Whitelist</Text>
                      <div className="text-sm text-gray-500">Restrict access to specific IP addresses</div>
                    </div>
                    <Switch />
                  </div>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="auditLogging" valuePropName="checked">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <Text strong>Audit Logging</Text>
                      <div className="text-sm text-gray-500">Log all user activities</div>
                    </div>
                    <Switch />
                  </div>
                </Form.Item>
              </Col>
            </Row>

            <div className="flex justify-between mt-6">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => handleResetSettings('security')}
              >
                Reset to Defaults
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
              >
                Save Changes
              </Button>
            </div>
          </Form>
        </Card>
      ),
    },
    {
      key: 'users',
      label: (
        <span>
          <UserOutlined />
          Users & Roles
        </span>
      ),
      children: (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <Title level={4} style={{ margin: 0 }}>Users & Roles</Title>
            <Button type="primary" onClick={showAddUser}>Add User</Button>
          </div>

          <Table
            rowKey="id"
            dataSource={users}
            columns={userColumns}
            pagination={{ pageSize: 5 }}
          />

          <RightDrawer
            title={editingUser ? 'Edit User' : 'Add User'}
            open={userDrawerOpen}
            onClose={() => setUserDrawerOpen(false)}
          >
            <Form
              id="drawer-form"
              form={userForm}
              layout="vertical"
              initialValues={{ role: 'Viewer' }}
              onFinish={handleSaveUser}
            >
              <Form.Item name="name" label="Full Name" rules={[{ required: true, message: 'Please enter name' }]}> 
                <Input placeholder="e.g., Jane Doe" />
              </Form.Item>
              <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}> 
                <Input placeholder="e.g., jane@example.com" />
              </Form.Item>
              <Form.Item name="role" label="Role" rules={[{ required: true }]}> 
                <Select options={roleOptions.map((r) => ({ value: r, label: r }))} />
              </Form.Item>
            </Form>
          </RightDrawer>
        </Card>
      ),
    },
    {
      key: 'system',
      label: (
        <span>
          <DatabaseOutlined />
          System
        </span>
      ),
      children: (
        <Card>
          <Form
            form={systemForm}
            layout="vertical"
            onFinish={(values) => handleSaveSettings('system', values)}
          >
            <Title level={4}>Backup & Maintenance</Title>
            
            <Alert
              message="System Configuration"
              description="Configure system-level settings including backups, maintenance, and performance."
              type="info"
              showIcon
              className="mb-6"
            />

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="autoBackup" valuePropName="checked">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <Text strong>Automatic Backup</Text>
                      <div className="text-sm text-gray-500">Enable scheduled backups</div>
                    </div>
                    <Switch />
                  </div>
                </Form.Item>
              </Col>
              <Col span={12}>
                <FormField
                  name="backupFrequency"
                  label="Backup Frequency"
                  type="select"
                  options={[
                    { value: 'hourly', label: 'Hourly' },
                    { value: 'daily', label: 'Daily' },
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'monthly', label: 'Monthly' },
                  ]}
                />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <FormField
                  name="dataRetention"
                  label="Data Retention (days)"
                  type="number"
                  min={30}
                  max={2555}
                  placeholder="Enter retention period"
                />
              </Col>
              <Col span={12}>
                <Form.Item name="maintenanceMode" valuePropName="checked">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <Text strong>Maintenance Mode</Text>
                      <div className="text-sm text-gray-500">Enable maintenance mode</div>
                    </div>
                    <Switch />
                  </div>
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Title level={4}>Performance</Title>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="cacheEnabled" valuePropName="checked">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <Text strong>Cache Enabled</Text>
                      <div className="text-sm text-gray-500">Enable application caching</div>
                    </div>
                    <Switch />
                  </div>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="debugMode" valuePropName="checked">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <Text strong>Debug Mode</Text>
                      <div className="text-sm text-gray-500">Enable debug logging</div>
                    </div>
                    <Switch />
                  </div>
                </Form.Item>
              </Col>
            </Row>

            <div className="flex justify-between mt-6">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => handleResetSettings('system')}
              >
                Reset to Defaults
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
              >
                Save Changes
              </Button>
            </div>
          </Form>
        </Card>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Settings</h1>
        <p className="text-gray-600">Configure your application preferences and system settings</p>
      </div>

      {/* Settings Tabs */}
      <Card>
        <Tabs
          defaultActiveKey="general"
          items={tabItems}
          tabPosition="top"
          className="min-h-96"
        />
      </Card>
    </div>
  );
};

export default Settings;
